<script setup lang="ts">
import { useBackToClose } from '@/composables/useBackToClose'
import { localizePlace } from '@/data/regions'
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  Search, X, Clock, Flame, Sparkles, TrendingUp,
  ArrowRight, Smartphone, Car, Building2, Wheat, Gem, ShieldCheck,
  MapPin, Gavel, Loader2, ImageOff, Mic, LayoutGrid
} from 'lucide-vue-next'
import { platformCategories } from '@/data/categories'
import { useI18n } from '@/composables/useI18n'
import { useFormatters } from '@/composables/useFormatters'
import { auctionService } from '@/services/auctionService'
import type { Auction, AuctionFilters } from '@/types'
import TundukMark from '@/components/icons/TundukMark.vue'

interface Props {
  modelValue: boolean
  // Search within one category (the header scope picker, or the category
  // page the visitor is on). Empty = all categories.
  category?: string
  // Open straight into voice input (the header mic button).
  startVoice?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const router = useRouter()
const { t, currentLocale, currentLang } = useI18n()
const { currency } = useFormatters()
const { formatMoney } = currency

const searchInputRef = ref<HTMLInputElement | null>(null)
const query = ref('')
const onlyLive = ref(false)

// Category scope: starts from the header's choice each time the palette opens,
// and can be cleared here without leaving the search.
const scope = ref('')
const scopeName = computed(() => {
  const c = platformCategories.find(x => x.slug === scope.value)
  return c ? ((c.name as Record<string, string>)[currentLang.value] || c.name.ru) : ''
})

// Voice input (Web Speech API). Offered only where the browser has it —
// Chrome/Edge/Samsung Internet on Android, Safari on iOS; recognition quality
// for Kyrgyz is weaker than for Russian, so ky falls back to ru-RU.
type SpeechRec = { lang: string; interimResults: boolean; maxAlternatives: number; start(): void; stop(): void; onresult: ((e: any) => void) | null; onend: (() => void) | null; onerror: ((e: any) => void) | null }
const SpeechCtor: (new () => SpeechRec) | undefined = typeof window !== 'undefined'
  ? ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
  : undefined
const voiceSupported = !!SpeechCtor
const isListening = ref(false)
let recognizer: SpeechRec | null = null
function startListening() {
  if (!SpeechCtor || isListening.value) return
  recognizer = new SpeechCtor()
  recognizer.lang = currentLang.value === 'tr' ? 'tr-TR' : 'ru-RU'
  recognizer.interimResults = true
  recognizer.maxAlternatives = 1
  recognizer.onresult = (e: any) => {
    const text = Array.from(e.results as ArrayLike<any>).map((r: any) => r[0]?.transcript || '').join(' ').trim()
    if (text) query.value = text
  }
  recognizer.onend = () => { isListening.value = false }
  recognizer.onerror = () => { isListening.value = false }
  isListening.value = true
  try { recognizer.start() } catch { isListening.value = false }
}
function stopListening() {
  try { recognizer?.stop() } catch { /* already stopped */ }
  isListening.value = false
}

// Live lot preview: a short debounced lookup so the palette shows real lots
// while the visitor types. Only the latest request may write into `results`.
const results = ref<Auction[]>([])
const isSearching = ref(false)
const hasQuery = computed(() => query.value.trim().length >= 2)
let searchTimer: ReturnType<typeof setTimeout> | null = null
let searchSeq = 0

async function fetchResults() {
  const term = query.value.trim()
  const seq = ++searchSeq
  if (term.length < 2) {
    results.value = []
    isSearching.value = false
    return
  }
  isSearching.value = true
  try {
    const filters: AuctionFilters = { search: term, perPage: 5 }
    if (onlyLive.value) filters.status = 'live'
    if (scope.value) filters.category = scope.value
    const res = await auctionService.getAuctions(filters)
    if (seq !== searchSeq) return
    results.value = Array.isArray(res?.data) ? res.data : []
  } catch {
    if (seq === searchSeq) results.value = []
  } finally {
    if (seq === searchSeq) isSearching.value = false
  }
}

watch([query, onlyLive, scope], () => {
  if (searchTimer) clearTimeout(searchTimer)
  // The fetch is debounced, so enter the loading state synchronously — otherwise
  // the "nothing found" panel flashes for the whole debounce window, before the
  // request has even been issued. Bumping the sequence at the same time drops
  // any in-flight response belonging to the previous term.
  searchSeq++
  if (query.value.trim().length >= 2) {
    isSearching.value = true
  } else {
    isSearching.value = false
    results.value = []
  }
  searchTimer = setTimeout(fetchResults, 250)
})

// --- Dialog keyboard behaviour -------------------------------------------
// Esc has to close the palette from anywhere inside it (not only while the text
// field holds focus), and Tab must stay within the overlay while it is open.
const panelRef = ref<HTMLElement | null>(null)

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusableNodes(): HTMLElement[] {
  if (!panelRef.value) return []
  return Array.from(panelRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement)
}

function onDialogKeydown(event: KeyboardEvent) {
  if (!props.modelValue) return

  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }

  if (event.key !== 'Tab') return
  const nodes = focusableNodes()
  if (!nodes.length) return

  const first = nodes[0]
  const last = nodes[nodes.length - 1]
  const active = document.activeElement as HTMLElement | null
  const inside = !!active && !!panelRef.value?.contains(active)

  if (!inside) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
  } else if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

onUnmounted(() => {
  if (searchTimer) clearTimeout(searchTimer)
  document.removeEventListener('keydown', onDialogKeydown)
})

const RECENT_SEARCHES_KEY = 'itorgo_recent_searches'

function loadRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveRecentSearches() {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.value))
  } catch {}
}

// Real, per-visitor search history — persisted locally, never seeded with
// fabricated examples (a first-time visitor should see an empty list, not
// searches they never made).
const recentSearches = ref<string[]>(loadRecentSearches())

const lang = computed(() => (currentLocale.value?.code as 'ky' | 'ru' | 'tr') || 'ru')

const TRENDING_TAGS: Record<'ru' | 'ky' | 'tr', string[]> = {
  ru: ['iPhone 17 Pro', 'Camry 70 Растаможен', 'Арашан 140кг', 'Дордой 12 проход', 'Lexus GX 460', 'MacBook M3 Max', 'Золото 585', 'PS5 Slim'],
  ky: ['iPhone 17 Pro', 'Camry 70 бажысы төлөнгөн', 'Арашан 140кг', 'Дордой 12-катар', 'Lexus GX 460', 'MacBook M3 Max', '585 сынар алтын', 'PS5 Slim'],
  tr: ['iPhone 17 Pro', 'Camry 70 Gümrüklü', 'Arashan 140kg', 'Dordoy 12. Geçit', 'Lexus GX 460', 'MacBook M3 Max', '585 Ayar Altın', 'PS5 Slim'],
}
const trendingTags = computed(() => TRENDING_TAGS[lang.value])

const QUICK_CATEGORIES: Record<'ru' | 'ky' | 'tr', Array<{ slug: string; label: string }>> = {
  ru: [
    { slug: 'electronics', label: 'Электроника' },
    { slug: 'vehicles', label: 'Автомобили' },
    { slug: 'livestock', label: 'Скотный рынок' },
    { slug: 'real-estate', label: 'Недвижимость' },
    { slug: 'jewelry', label: 'Ювелирные' },
  ],
  ky: [
    { slug: 'electronics', label: 'Электроника' },
    { slug: 'vehicles', label: 'Автоунаалар' },
    { slug: 'livestock', label: 'Мал базары' },
    { slug: 'real-estate', label: 'Кыймылсыз мүлк' },
    { slug: 'jewelry', label: 'Зер буюмдар' },
  ],
  tr: [
    { slug: 'electronics', label: 'Elektronik' },
    { slug: 'vehicles', label: 'Otomobil' },
    { slug: 'livestock', label: 'Hayvan Pazarı' },
    { slug: 'real-estate', label: 'Emlak' },
    { slug: 'jewelry', label: 'Mücevher' },
  ],
}
const categoryIcons: Record<string, any> = {
  electronics: Smartphone,
  vehicles: Car,
  livestock: Wheat,
  'real-estate': Building2,
  jewelry: Gem,
}
const quickCategories = computed(() =>
  QUICK_CATEGORIES[lang.value].map(c => ({ ...c, icon: categoryIcons[c.slug] }))
)

const AI_PROMPTS: Record<'ru' | 'ky' | 'tr', string[]> = {
  ru: [
    'iPhone в Бишкеке до 50 000 сом',
    'Toyota Camry с гарантией и растаможкой',
    'Племенной баран Арашан с ветпаспортом',
    'Торговый контейнер на Дордое до 1.5 млн сом',
  ],
  ky: [
    'Бишкекте 50 000 сомго чейин iPhone',
    'Кепилдиги жана бажысы төлөнгөн Toyota Camry',
    'Ветпаспорту бар Арашан породалуу кочкор',
    'Дордойдо 1.5 млн сомго чейин соода контейнери',
  ],
  tr: [
    'Bişkek\'te 50.000 som\'a kadar iPhone',
    'Garantili ve gümrüklü Toyota Camry',
    'Vet pasaportlu Arashan damızlık koç',
    'Dordoy\'da 1.5 milyon som\'a kadar ticari konteyner',
  ],
}
const aiPrompts = computed(() => AI_PROMPTS[lang.value])

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    scope.value = props.category || ''
    nextTick(() => {
      searchInputRef.value?.focus()
      if (props.startVoice) startListening()
    })
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onDialogKeydown)
  } else {
    stopListening()
    document.body.style.overflow = ''
    document.removeEventListener('keydown', onDialogKeydown)
  }
})

function close() {
  emit('update:modelValue', false)
}

// Phone back button closes this overlay instead of leaving the page.
useBackToClose(() => props.modelValue, () => close())

function rememberSearch(searchTerm: string) {
  // Add to recent if not exists
  if (!recentSearches.value.includes(searchTerm)) {
    recentSearches.value.unshift(searchTerm)
    if (recentSearches.value.length > 6) recentSearches.value.pop()
    saveRecentSearches()
  }
}

function handleSearch(term?: string) {
  const searchTerm = (term !== undefined ? term : query.value).trim()
  if (!searchTerm) return

  rememberSearch(searchTerm)

  const queryParams: Record<string, string> = { search: searchTerm }
  if (onlyLive.value) {
    queryParams.status = 'live'
  }
  if (scope.value) queryParams.category = scope.value

  close()
  router.push({ path: '/auctions', query: queryParams })
}

function removeRecent(index: number) {
  recentSearches.value.splice(index, 1)
  saveRecentSearches()
}

function selectCategory(slug: string) {
  close()
  router.push({ path: '/auctions', query: { category: slug } })
}

function openLot(id: string) {
  const term = query.value.trim()
  if (term) rememberSearch(term)
  close()
  router.push(`/auctions/${id}`)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex flex-col items-center justify-end bg-ink/60 p-0 backdrop-blur-sm sm:justify-start sm:p-4 sm:pt-16 lg:pt-20"
        @click.self="close"
      >
        <!-- Panel: bottom sheet on mobile, top-anchored palette on desktop -->
        <div
          ref="panelRef"
          class="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[1.5rem] bg-surface shadow-[0_24px_60px_-24px_rgba(20,23,31,0.6)] animate-slide-up sm:max-h-[85vh] sm:max-w-2xl sm:rounded-[1.5rem] sm:animate-scale-in"
          role="dialog"
          aria-modal="true"
          :aria-label="t('common.search')"
        >
          <!-- Drag handle (mobile sheet) -->
          <div class="mx-auto my-2.5 h-1.5 w-12 shrink-0 rounded-full bg-border sm:hidden" />

          <!-- Search input -->
          <div class="border-b border-border p-4 sm:p-5">
            <div class="flex items-center gap-2 rounded-xl border border-border bg-surface px-3.5 py-2 transition focus-within:border-ink focus-within:ring-2 focus-within:ring-primary/40">
              <Search class="h-5 w-5 shrink-0 text-text-muted" />
              <input
                ref="searchInputRef"
                v-model="query"
                type="text"
                :placeholder="t('nav.searchPlaceholder') || 'Что вы ищете? Например: iPhone, Camry, Арашан...'"
                class="min-w-0 flex-1 bg-transparent py-1 text-sm font-medium text-text-primary placeholder:text-text-muted focus:outline-none sm:text-base"
                @keydown.enter="handleSearch()"
              />
              <button
                v-if="query"
                type="button"
                class="rounded-full p-1 text-text-muted transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :aria-label="t('common.clear')"
                @click="query = ''"
              >
                <X class="h-4 w-4" />
              </button>
              <button
                v-if="voiceSupported"
                type="button"
                class="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :class="isListening ? 'bg-primary text-onPrimary' : 'text-primary hover:bg-primary/10'"
                :aria-label="isListening ? t('headerBar.voiceStop') : t('headerBar.voiceSearch')"
                :aria-pressed="isListening"
                @click="isListening ? stopListening() : startListening()"
              >
                <span v-if="isListening" class="absolute inset-0 animate-ping rounded-full bg-primary/40 motion-reduce:hidden" />
                <Mic class="relative h-4 w-4" />
              </button>
              <span class="hidden shrink-0 items-center gap-1 rounded-full bg-sun/30 px-2.5 py-1 text-[11px] font-semibold text-onSun sm:inline-flex">
                <Sparkles class="h-3 w-3" />
                <span>{{ t('ui.header.s2') }}</span>
              </span>
              <button
                type="button"
                class="inline-flex min-h-[40px] shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-bold text-onPrimary transition hover:bg-primary-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40 sm:min-h-0"
                @click="handleSearch()"
              >
                <span>{{ t('ui.searchModal.s1') }}</span>
                <ArrowRight class="h-3.5 w-3.5" />
              </button>
            </div>

            <!-- Filters strip -->
            <div class="no-scrollbar mt-3 flex items-center gap-2 overflow-x-auto">
              <button
                v-if="scope"
                type="button"
                class="inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-full bg-primary/10 px-3.5 py-1 text-[11px] font-semibold text-primary-hover ring-1 ring-primary/25 transition hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0 sm:px-2.5"
                :aria-label="`${t('headerBar.searchIn')} ${scopeName} — ${t('common.clear')}`"
                @click="scope = ''"
              >
                <LayoutGrid class="h-3.5 w-3.5" />
                <span>{{ t('headerBar.searchIn') }} {{ scopeName }}</span>
                <X class="h-3 w-3" />
              </button>
              <button
                type="button"
                class="inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1 text-[11px] font-bold uppercase transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0 sm:px-2.5"
                :class="onlyLive
                  ? 'bg-success/10 text-success ring-1 ring-success/30'
                  : 'bg-accent text-text-secondary hover:bg-accent-hover hover:text-text-primary'"
                :aria-pressed="onlyLive"
                @click="onlyLive = !onlyLive"
              >
                <span v-if="onlyLive" class="relative inline-flex h-1.5 w-1.5 rounded-full bg-success live-dot" />
                <span v-else class="inline-flex h-1.5 w-1.5 rounded-full bg-success" />
                <span>{{ t('ui.searchModal.s2') }}</span>
              </button>
              <span class="inline-flex shrink-0 items-center gap-1 px-1 text-[11px] text-text-muted">
                <ShieldCheck class="h-3.5 w-3.5 text-success" />
                <span>{{ t('ui.searchModal.s3') }}</span>
              </span>
            </div>
          </div>

          <!-- Scroll body -->
          <div class="flex-1 space-y-6 overflow-y-auto overscroll-contain p-4 sm:p-5">
            <!-- Live results. The region itself is always in the DOM (a live
                 region created at the same moment as its content is not
                 announced); only its contents are gated on the query. -->
            <section aria-live="polite" :aria-busy="isSearching">
              <template v-if="hasQuery">
              <div class="mb-2.5 flex items-center justify-between gap-2">
                <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
                  <Search class="h-3.5 w-3.5" />
                  <span>{{ t('ui.searchModal.results') }}</span>
                </span>
                <Loader2 v-if="isSearching" class="h-3.5 w-3.5 animate-spin text-text-muted" aria-hidden="true" />
              </div>

              <ul v-if="results.length" class="overflow-hidden rounded-card border border-border bg-surface">
                <li v-for="lot in results" :key="lot.id" class="border-b border-border last:border-0">
                  <button
                    type="button"
                    class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    @click="openLot(lot.id)"
                  >
                    <img
                      v-if="lot.images?.[0]"
                      :src="lot.images[0]"
                      :alt="lot.title"
                      class="h-12 w-14 shrink-0 rounded-lg bg-accent object-cover"
                      loading="lazy"
                    />
                    <span v-else class="inline-flex h-12 w-14 shrink-0 items-center justify-center rounded-lg bg-accent text-text-muted">
                      <ImageOff class="h-4 w-4" />
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-[13px] font-semibold text-text-primary">{{ lot.title }}</span>
                      <span class="mt-0.5 flex items-center gap-1.5 text-[11px] text-text-muted">
                        <MapPin class="h-3 w-3 shrink-0" />
                        <span class="truncate">{{ localizePlace(lot.city, currentLang) }}</span>
                        <span class="text-border">·</span>
                        <Gavel class="h-3 w-3 shrink-0" />
                        <span class="tabular">{{ lot.bidCount }}</span>
                      </span>
                    </span>
                    <span class="shrink-0 font-display text-sm font-bold tabular text-text-primary">{{ formatMoney(lot.currentPrice) }}</span>
                  </button>
                </li>
              </ul>

              <!-- Loading: skeleton rows, shown from the keystroke through the
                   debounce window until the response lands. -->
              <div
                v-else-if="isSearching"
                class="overflow-hidden rounded-card border border-border bg-surface"
                aria-hidden="true"
              >
                <div
                  v-for="i in 3"
                  :key="i"
                  class="flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-0"
                >
                  <div class="h-12 w-14 shrink-0 animate-pulse rounded-lg bg-accent" />
                  <div class="min-w-0 flex-1 space-y-2">
                    <div class="h-3 w-2/3 animate-pulse rounded-full bg-accent" />
                    <div class="h-2.5 w-1/3 animate-pulse rounded-full bg-accent" />
                  </div>
                  <div class="h-4 w-16 shrink-0 animate-pulse rounded-full bg-accent" />
                </div>
              </div>

              <div v-else class="rounded-card border border-dashed border-border p-6 text-center text-sm text-text-secondary">
                {{ t('ui.searchModal.noResults', { q: query.trim() }) }}
              </div>

              <button
                v-if="results.length"
                type="button"
                class="mt-2.5 inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-text-primary transition hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0 sm:px-1"
                @click="handleSearch()"
              >
                <span>{{ t('common.viewAll') }}</span>
                <ArrowRight class="h-3.5 w-3.5" />
              </button>
              </template>
            </section>

            <!-- Recent searches -->
            <section v-if="recentSearches.length > 0">
              <div class="mb-2.5 flex items-center justify-between gap-2">
                <span class="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
                  <Clock class="h-3.5 w-3.5" />
                  <span>{{ t('ui.searchModal.s4') }}</span>
                </span>
                <button
                  type="button"
                  class="rounded-lg px-2 py-1 text-[11px] font-semibold text-text-secondary transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  @click="recentSearches = []; saveRecentSearches()"
                >
                  {{ t('ui.searchModal.s5') }}
                </button>
              </div>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(item, idx) in recentSearches"
                  :key="item"
                  class="inline-flex items-center gap-1 rounded-full bg-accent py-1 pl-3 pr-1.5 text-xs font-semibold text-text-secondary transition hover:bg-accent-hover hover:text-text-primary"
                >
                  <button
                    type="button"
                    class="focus-visible:underline focus-visible:outline-none"
                    @click="handleSearch(item)"
                  >
                    {{ item }}
                  </button>
                  <button
                    type="button"
                    class="rounded-full p-0.5 text-text-muted transition hover:bg-surface hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    :aria-label="t('common.delete')"
                    @click.stop="removeRecent(idx)"
                  >
                    <X class="h-3 w-3" />
                  </button>
                </span>
              </div>
            </section>

            <!-- Trending searches -->
            <section>
              <div class="mb-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
                <Flame class="h-3.5 w-3.5 text-primary-hover" />
                <span>{{ t('ui.searchModal.s6') }}</span>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="tag in trendingTags"
                  :key="tag"
                  type="button"
                  class="inline-flex min-h-[40px] items-center gap-1 rounded-full bg-primary/20 px-3 py-1.5 text-xs font-semibold text-onPrimaryContainer transition hover:bg-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0"
                  @click="handleSearch(tag)"
                >
                  <TrendingUp class="h-3 w-3" />
                  <span>{{ tag }}</span>
                </button>
              </div>
            </section>

            <!-- Category shortcuts -->
            <section>
              <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
                {{ t('ui.searchModal.s7') }}
              </div>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <button
                  v-for="cat in quickCategories"
                  :key="cat.slug"
                  type="button"
                  class="group flex items-center gap-2.5 rounded-xl border border-border bg-surface p-2.5 text-left transition hover:-translate-y-0.5 hover:border-ink/15 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  @click="selectCategory(cat.slug)"
                >
                  <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/20 text-onPrimaryContainer">
                    <component :is="cat.icon" class="h-4 w-4" />
                  </span>
                  <span class="truncate text-xs font-semibold text-text-primary">{{ cat.label }}</span>
                </button>
              </div>
            </section>

            <!-- AI smart queries -->
            <section class="relative overflow-hidden rounded-card bg-ink p-4 text-white">
              <TundukMark class="pointer-events-none absolute -bottom-20 -right-12 h-56 w-56 text-primary/10" />
              <div class="relative space-y-3">
                <div class="flex items-center gap-2.5">
                  <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-onPrimary">
                    <Sparkles class="h-4 w-4" />
                  </span>
                  <h3 class="font-display text-[15px] font-semibold text-white">{{ t('ui.searchModal.s8') }}</h3>
                </div>
                <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    v-for="prompt in aiPrompts"
                    :key="prompt"
                    type="button"
                    class="group flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2.5 text-left text-xs font-medium text-white transition hover:border-white/20 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    @click="handleSearch(prompt)"
                  >
                    <span class="truncate">{{ prompt }}</span>
                    <ArrowRight class="h-3.5 w-3.5 shrink-0 text-white/50 transition group-hover:text-primary" />
                  </button>
                </div>
              </div>
            </section>
          </div>

          <!-- Footer bar -->
          <div class="flex items-center justify-between gap-3 border-t border-border bg-background px-4 py-3 text-xs text-text-muted sm:px-5">
            <span class="hidden sm:inline">
              {{ t('ui.searchModal.s9') }}
              <kbd class="rounded-md border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px] text-text-secondary">ESC</kbd>
              {{ t('ui.searchModal.s10') }}
            </span>
            <button
              type="button"
              class="inline-flex min-h-[40px] w-full items-center justify-center rounded-xl border border-border bg-surface px-3.5 py-2 text-xs font-semibold text-text-primary transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-0 sm:w-auto"
              @click="close"
            >
              {{ t('ui.searchModal.s11') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
.animate-slide-up {
  animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleIn {
  from { transform: scale(0.97); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scale-in {
  animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
@media (min-width: 640px) {
  .sm\:animate-scale-in {
    animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
}
</style>
